import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { Modal, Spin, Alert, Select as AntdSelect, message } from 'antd';
import ReactSelect from 'react-select';
import LoadThongTinService from '../../services/LoadThongTinService';
import ManagerService from '../../services/ManagerService';
import MapPreview from './MapPreview';
import 'antd/dist/reset.css';
import './style.css';

const GanChuyenKinhDoanhComponent = () => {
  // States cho chuyến kinh doanh
  const [chuyenData, setChuyenData] = useState([]);
  const [bienKiemSoatOptions, setBienKiemSoatOptions] = useState([]);
  const [taiXeOptions, setTaiXeOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('All');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);

  // States cho modal gán chuyến
  const [selectedChuyen, setSelectedChuyen] = useState(null);
  const [showRouteModal, setShowRouteModal] = useState(false);
  const [assignBienKiemSoat, setAssignBienKiemSoat] = useState(null);
  const [assignTaiXe, setAssignTaiXe] = useState(null);
  const [assignTaiXe2, setAssignTaiXe2] = useState(null);

  // States cho điểm bắt đầu mới (autocomplete)
  const [newOrigin, setNewOrigin] = useState('');
  const [originOptions, setOriginOptions] = useState([]);

  // Hàm tải lại dữ liệu chuyến kinh doanh
  const refreshChuyenData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await LoadThongTinService.getChuyenKinhDoanh();
      if (response && response.data) {
        const sortedData = response.data.sort((a, b) => {
          const aHasInfo = a.bien_kiem_soat && a.tai_xe;
          const bHasInfo = b.bien_kiem_soat && b.tai_xe;
          return aHasInfo === bHasInfo ? b.id - a.id : aHasInfo ? 1 : -1;
        });
        setChuyenData(sortedData);
      }
    } catch (error) {
      console.error('Lỗi khi tải dữ liệu:', error);
      setErrorMessage('Không thể tải dữ liệu chuyến kinh doanh.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshChuyenData();
  }, [refreshChuyenData]);

  // Lấy dữ liệu biển kiểm soát
  useEffect(() => {
    const fetchBienKiemSoat = async () => {
      try {
        const response = await LoadThongTinService.getBienKiemSoat();
        const options = response.data.map(item => ({ value: item, label: item }));
        setBienKiemSoatOptions(options);
      } catch (error) {
        console.error('Error fetching biển kiểm soát:', error);
      }
    };
    fetchBienKiemSoat();
  }, []);

  // Lấy dữ liệu tài xế
  useEffect(() => {
    const fetchTaiXe = async () => {
      try {
        const response = await LoadThongTinService.getGanUsernameChuyenXe();
        const options = response.data.map(item => ({
          value: `${item[0]} - (${item[1]})`,
          label: `${item[0]} - (${item[1]}) - ${item[2]}`,
        }));
        setTaiXeOptions(options);
      } catch (error) {
        console.error('Error fetching tài xế:', error);
      }
    };
    fetchTaiXe();
  }, []);

  // Tạo danh sách khách hàng cho dropdown
  const customerOptions = [
    { value: 'All', label: 'Tất cả khách hàng' },
    ...Array.from(new Set(chuyenData.map(chuyen => chuyen.ten_khach_hang))).map(customer => ({
      value: customer,
      label: customer,
    })),
  ];

  const filteredChuyenData = chuyenData.filter(
    chuyen =>
      (selectedCustomer === 'All' || chuyen.ten_khach_hang === selectedCustomer) &&
      (selectedDate === null || new Date(chuyen.ngay_van_chuyen).toLocaleDateString() === new Date(selectedDate).toLocaleDateString())
  );

  const handleSelectDate = useCallback(value => {
    setSelectedDate(value);
  }, []);

  // Handler chọn khách hàng
  const handleSelectCustomer = useCallback(value => {
    setSelectedCustomer(value);
  }, []);

  // Mở modal để xem routing và gán chuyến
  const handleViewRouting = useCallback(chuyen => {
    setSelectedChuyen(chuyen);
    setAssignBienKiemSoat(null);
    setAssignTaiXe(null);
    setNewOrigin('');
    setOriginOptions([]);
    setShowRouteModal(true);
  }, []);

  // Hàm autocomplete điểm bắt đầu mới sử dụng API của Goong Map
  const fetchOriginOptions = async input => {
    if (!input) return [];
    try {
      const res = await fetch(
        `https://rsapi.goong.io/Place/AutoComplete?api_key=${process.env.REACT_APP_GOONG_API_KEY}&input=${encodeURIComponent(input)}`
      );
      const data = await res.json();
      if (data && data.predictions) {
        return data.predictions.map(item => ({
          value: item.description,
          label: item.description,
        }));
      }
      return [];
    } catch (error) {
      console.error("Error fetching autocomplete:", error);
      return [];
    }
  };

  const handleOriginSearch = async value => {
    if (!value) {
      setOriginOptions([]);
      return;
    }
    const options = await fetchOriginOptions(value);
    setOriginOptions(options);
  };

  // Hàm cập nhật định tuyến: chuyển đổi newOrigin thành tọa độ và sử dụng tọa độ đó trong API định tuyến
  const handleUpdateRoute = async () => {
    if (!newOrigin) {
      message.error("Vui lòng chọn điểm bắt đầu mới");
      return;
    }
    try {
      // Chuyển đổi địa chỉ điểm bắt đầu thành tọa độ
      const originCoords = await ManagerService.getCoordinatesFromAddress(newOrigin);

      // Lấy địa chỉ điểm đến, ưu tiên dùng diem_den_thuc_te nếu có, hoặc diem_den
      const destinationAddress = selectedChuyen.diem_den_thuc_te || selectedChuyen.diem_den;
      // Chuyển đổi địa chỉ điểm đến thành tọa độ
      const destinationCoords = await ManagerService.getCoordinatesFromAddress(destinationAddress);
      // Tạo chuỗi điểm đến theo định dạng "lat,lng" cho API định tuyến
      const destinationStr = `${destinationCoords.lat},${destinationCoords.lng}`;

      // Gọi API định tuyến sử dụng tọa độ đã chuyển đổi
      const response = await ManagerService.getRouting(originCoords, destinationStr);
      if (response.success) {
        setSelectedChuyen(prev => ({
          ...prev,
          routing: response.data.routePolyline,
          diem_di_thuc_te: newOrigin,
          originCoords: originCoords,
          destinationCoords: destinationCoords, // nếu cần lưu lại tọa độ điểm đến
        }));
        message.success("Định tuyến đã được cập nhật");
      } else {
        message.error("Không thể cập nhật định tuyến: " + response.message);
      }
    } catch (error) {
      message.error("Lỗi khi cập nhật định tuyến: " + error.message);
    }
  };

  // Khi gán chuyến, thêm trường "diem_di_thay_doi" để lưu điểm đi thay đổi
  const handleAssignChuyen = async () => {
    if (!assignBienKiemSoat || !assignTaiXe) {
      message.error('Vui lòng chọn biển kiểm soát và tài xế.');
      return;
    }
    try {
      let taiXe2 = ""
      if (!assignTaiXe2) {
        taiXe2 = ""
      }
      else {
        taiXe2 = assignTaiXe2.value;
      }
      const formDataAssign = new FormData();
      formDataAssign.append('chon_xe', assignBienKiemSoat.value);
      formDataAssign.append('chon_tai_xe', assignTaiXe.value);
      formDataAssign.append('chon_tai_xe2', taiXe2);
      // Gửi thêm trường diem_di_thay_doi với giá trị là newOrigin
      formDataAssign.append('diem_di_thay_doi', newOrigin);

      const response = await ManagerService.ganThemThongTin(selectedChuyen.id, formDataAssign);
      if (response.success) {
        message.success(`Gán chuyến thành công: ${response.message}`);
        setShowRouteModal(false);
        refreshChuyenData();
      } else {
        message.error(`Gán chuyến thất bại: ${response.message}`);
      }
    } catch (error) {
      console.error('Lỗi khi gán chuyến:', error);
      message.error('Lỗi khi gán chuyến.');
    }
  };

  const handleHuyGanChuyen = async maChuyen => {
    try {
      const response = await ManagerService.huyGanChuyenKinhDoanh(maChuyen);
      if (response.success) {
        message.success('Hủy gán chuyến thành công!');
        refreshChuyenData();
      } else {
        message.error(`Lỗi: ${response.message}`);
      }
    } catch (error) {
      console.error('Lỗi khi hủy gán chuyến:', error);
      message.error('Lỗi khi hủy gán chuyến.');
    }
  };

  const handleHuyChuyen = async maChuyen => {
    try {
      const response = await ManagerService.huyChuyenKinhDoanh(maChuyen);
      if (response.success) {
        message.success('Chuyến kinh doanh đã được hủy thành công!');
        refreshChuyenData();
      } else {
        message.error(`Lỗi: ${response.message}`);
      }
    } catch (error) {
      console.error('Lỗi khi hủy chuyến:', error);
      message.error('Không thể hủy chuyến kinh doanh.');
    }
  };

  const formattedNumber = (number) => {
    if (number === null || number === undefined) return '';
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  if (loading) {
    return (
      <Container className="text-center mt-5">
        <Spin tip="Đang tải dữ liệu..." size="large" />
      </Container>
    );
  }

  return (
    <Container fluid className="mt-4 GanChuyenKinhDoanhComponent">
      <h2 className="text-center mb-4">Gán Chuyến Cho Tài Xế</h2>
      {errorMessage && (
        <Alert message={errorMessage} type="error" showIcon style={{ marginBottom: '1rem' }} />
      )}
      <div style={{ marginBottom: '1rem', display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly' }}>
        <AntdSelect
          options={customerOptions}
          defaultValue="All"
          onChange={handleSelectCustomer}
          placeholder="Chọn khách hàng"
          style={{ height: "2.2em", width: "30%" }}
        />

        <Form.Control
          type="date"
          value={selectedDate || ''}
          onChange={(e) => handleSelectDate(e.target.value)}
          placeholder="Chọn ngày vận chuyển"
          style={{ height: "2.2em", width: "30%" }}
        />
      </div>
      <Row xs={1} sm={2} md={3} lg={3} xl={4} className="g-4">
        {filteredChuyenData.map(chuyen => (
          <Col key={chuyen.id}>
            <Card className="shadow-sm mb-3">
              <Card.Body className="text-center">
                <Card.Title className="fw-bold">Mã Chuyến: {chuyen.id}</Card.Title>
                <Card.Text>{chuyen.ten_khach_hang}</Card.Text>
                <Card.Text>
                  Ngày vận chuyển: {new Date(chuyen.ngay_van_chuyen).toLocaleDateString()}
                </Card.Text>
                <Card.Text>Điểm đi: {chuyen.diem_di}</Card.Text>
                <Card.Text>Điểm đến: {chuyen.diem_den}</Card.Text>
                <Card.Text>Loại xe: {chuyen.loai_xe}</Card.Text>
                <Card.Text>Trọng tải: {chuyen.trong_tai}</Card.Text>
                <Card.Text>Doanh thu: {formattedNumber(chuyen.gia_ban)}</Card.Text>
                <Card.Text>Chi phí phát sinh: {formattedNumber(chuyen.chi_phi_phat_sinh || 0)}</Card.Text>
                <Card.Text>Ghi chú : {chuyen.ghi_chu}</Card.Text>
                {(!chuyen.bien_kiem_soat || !chuyen.tai_xe) ? (
                  <div>
                    <Button style={{ marginBottom: '5px' }} type="primary" onClick={() => handleViewRouting(chuyen)}>
                      Xem Routing &amp; Gán
                    </Button>
                    <Button variant="danger" onClick={() => handleHuyChuyen(chuyen.id)}>
                      Hủy chuyến
                    </Button>
                  </div>
                ) : (
                  <>
                    <Card.Text>Biển kiểm soát: {chuyen.bien_kiem_soat}</Card.Text>
                    <Card.Text>Tài xế 1: {chuyen.tai_xe}</Card.Text>
                    <Card.Text>Tài xế 2: {chuyen.tai_xe_2 || "Không có"}</Card.Text>
                    <Button
                      type="primary"
                      onClick={() => handleHuyGanChuyen(chuyen.id)}
                      style={{ marginRight: '0.5rem', marginBottom: '5px' }}
                    >
                      Hủy Gán
                    </Button>
                    <Button variant="danger" onClick={() => handleHuyChuyen(chuyen.id)}>
                      Hủy chuyến
                    </Button>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
      <Modal
        visible={showRouteModal}
        onCancel={() => setShowRouteModal(false)}
        onOk={handleAssignChuyen}
        title="Preview Routing và Gán Thông Tin"
        width={800}
        okText="Xác nhận gán"
        cancelText="Hủy"
      >
        {selectedChuyen && (
          <>
            <Form.Group className="mb-3">
              <Form.Label>Điểm bắt đầu mới</Form.Label>
              <AntdSelect
                showSearch
                placeholder="Nhập địa chỉ điểm bắt đầu"
                value={newOrigin}
                onSearch={handleOriginSearch}
                onChange={(value) => setNewOrigin(value)}
                filterOption={false}
                options={originOptions}
                style={{ width: '100%' }}
              />
            </Form.Group>
            <Button variant="info" onClick={handleUpdateRoute} style={{ marginBottom: '1rem' }}>
              Cập nhật định tuyến
            </Button>
            <div style={{ marginTop: '1rem' }}>
              <MapPreview
                routePolyline={selectedChuyen.routing}
                originAddress={selectedChuyen.diem_di_thuc_te || ''}
                destinationAddress={selectedChuyen.diem_den_thuc_te || ''}
              />
            </div>
            <div style={{ marginTop: '1rem' }}>
              <AntdSelect
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={bienKiemSoatOptions}
                placeholder="Chọn biển kiểm soát"
                onChange={(value, option) =>
                  setAssignBienKiemSoat({ value, label: option.label })
                }
                size="large"
                style={{ width: '100%', marginBottom: '1rem' }}
                value={assignBienKiemSoat ? assignBienKiemSoat.value : undefined}
              />
              <AntdSelect
                showSearch
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={taiXeOptions}
                placeholder="Chọn tài xế"
                onChange={(value, option) =>
                  setAssignTaiXe({ value, label: option.label })
                }
                size="large"
                style={{ width: '100%', marginBottom: '1rem' }}
                value={assignTaiXe ? assignTaiXe.value : undefined}
              />
              <AntdSelect
                showSearch
                allowClear
                optionFilterProp="label"
                filterOption={(input, option) =>
                  (option.label || '').toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
                options={taiXeOptions}
                placeholder="Chọn tài xế 2"
                onChange={(value, option) =>
                  setAssignTaiXe2(value ? { value, label: option?.label || '' } : null)
                }
                size="large"
                style={{ width: '100%' }}
                value={assignTaiXe2 ? assignTaiXe2.value : undefined}
              />
            </div>
          </>
        )}
      </Modal>
    </Container>
  );
};

export default GanChuyenKinhDoanhComponent;
